import { useState, useEffect } from 'react';
import { socketService } from '../services/socket';
import { useAuth } from '../context/AuthContext';
import { notificationService } from '../services/notificationService';
import { authFetch } from '../utils/authFetch';

interface Contact {
  id: string;
  phoneNumber: string;
  name?: string;
  avatar?: string;
  isBlocked: boolean;
  tags: string[];
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

interface Chat {
  id: string;
  contactId: string;
  assignedAgentId?: string;
  status: 'OPEN' | 'PENDING' | 'RESOLVED' | 'CLOSED';
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  subject?: string;
  lastMessageAt: string;
  createdAt: string;
  updatedAt: string;
  contact: Contact;
  messages?: any[];
  unreadCount?: number;
}

export const useChats = () => {
  const { user } = useAuth();
  const [chats, setChats] = useState<Chat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadChats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // authFetch automatically prepends /api
      console.log('Trying to fetch from: /chat');
      
      const response = await authFetch('/chat', {
        method: 'GET',
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('API Response:', data);
        
        if (data.success && Array.isArray(data.data)) {
          if (data.data.length > 0) {
            const chatsWithUnread = data.data.map((chat: Chat) => ({
              ...chat,
              unreadCount: chat.unreadCount || 0
            }));
            setChats(chatsWithUnread);
            setError(null);
            return;
          } else {
            console.log('Database is empty, using mock data');
            throw new Error('Database empty');
          }
        }
      }
      
      throw new Error('API not available');
    } catch (err) {
      console.log('Loading mock data:', err);
      if (err instanceof Error && err.message === 'Session expired') {
        return;
      }
      setError('Failed to load conversations - ' + (err instanceof Error ? err.message : 'API connection failed'));
      setChats([]);
    } finally {
      setLoading(false);
    }
  };

  // Socket.io real-time updates
  useEffect(() => {
    // Connect to socket
    socketService.connect();

    // Listen for chat updates
    const handleChatUpdate = (data: any) => {
      console.log('Real-time chat update:', data);
      loadChats(); // Reload chat list when there are updates
    };

    const handleNewWhatsAppMessage = (data: any) => {
      console.log('Real-time new WhatsApp message:', data);
      
      // Show notification for new WhatsApp messages
      console.log('Attempting to show notification for:', data.contact?.name || data.contact?.phoneNumber);
      try {
        notificationService.notifyNewMessage(
          data.contact?.name || data.contact?.phoneNumber || 'WhatsApp Contact',
          data.message?.content || 'New message received',
          data.chatId
        );
        console.log('Notification triggered successfully');
      } catch (error) {
        console.error('Notification error:', error);
      }
      
      loadChats(); // Reload chat list to show new message preview
    };

    const handleMessagesRead = (data: any) => {
      console.log('Messages marked as read:', data);
      loadChats(); // Reload chat list to update unread counts
    };

    socketService.on('chat_updated', handleChatUpdate);
    socketService.on('new_whatsapp_message', handleNewWhatsAppMessage);
    socketService.on('new_message', handleChatUpdate);
    socketService.on('messages_read', handleMessagesRead);

    return () => {
      socketService.off('chat_updated', handleChatUpdate);
      socketService.off('new_whatsapp_message', handleNewWhatsAppMessage);
      socketService.off('new_message', handleChatUpdate);
      socketService.off('messages_read', handleMessagesRead);
    };
  }, []);

  useEffect(() => {
    loadChats();
  }, []);

  return { chats, loading, error, setChats, refetch: loadChats };
};
